// ────────────────────────────────────────────────────────────────────────────
// Line-items band inspector.
//
// Split out of `BandInspector` because this band carries more than the rest put
// together: fifteen columns, four of which have their own options (which date,
// which end of the journey, which reference slot, product vs temperature).
//
// Two things this panel has to be honest about:
//
//  * Every transport column now resolves at generation, so there is no
//    "not connected yet" caveat left. A cell still prints empty when the order
//    simply has no such value — no trailer assigned, no container on the cargo —
//    which is correct rather than a gap.
//  * Width. Fifteen available columns on an A4 page runs out of room fast, so
//    the remaining width for the stretch column is shown live (see
//    `lib/pageMetrics.ts`).
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganisation } from '@my/directory-api/hooks';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  AlignPicker,
  Checkbox,
  InspectorGroup,
  InspectorRow,
  MoveButtons,
  NumberField,
  SelectField,
  TextField,
} from './controls';
import { moveItem } from '../lib/arrays';
import { measureColumns, resolveFlexKey } from '../lib/pageMetrics';
import {
  LINE_CARGO_MODES,
  LINE_CARGO_MODE_LABEL,
  LINE_COLUMN_LABEL,
  LINE_DATE_FORMATS,
  LINE_DATE_SOURCES,
  LINE_DATE_SOURCE_LABEL,
  LINE_DETAILS_MODES,
  LINE_DETAILS_MODE_LABEL,
  PRODUCTS_DISPLAYS,
  PRODUCTS_DISPLAY_LABEL,
  type LineCargoMode,
  type ProductsDisplay,
  type LineColumn,
  type LineDateFormat,
  type LineDateSource,
  type LineDetailsMode,
  type LinesBand,
  type PageSettings,
} from '../lib/types';

/** Reference slots an order actually has as indexed columns. */
const REFERENCE_SLOTS = [1, 2, 3, 4] as const;

function ColumnOptions({
  col,
  referenceLabels,
  onChange,
}: {
  col: LineColumn;
  referenceLabels: string[];
  onChange: (next: LineColumn) => void;
}) {
  if (col.key === 'date') {
    return (
      <>
        <InspectorRow label="Which date">
          <SelectField<LineDateSource>
            value={col.dateSource ?? 'collection'}
            options={LINE_DATE_SOURCES.map((v) => ({ value: v, label: LINE_DATE_SOURCE_LABEL[v] }))}
            onChange={(dateSource) => onChange({ ...col, dateSource })}
          />
        </InspectorRow>
        <InspectorRow label="Format">
          <SelectField<LineDateFormat>
            value={col.dateFormat ?? 'dd/MM/yyyy'}
            options={LINE_DATE_FORMATS.map((v) => ({ value: v, label: v }))}
            onChange={(dateFormat) => onChange({ ...col, dateFormat })}
          />
        </InspectorRow>
      </>
    );
  }

  if (col.key === 'description') {
    return (
      <>
        <InspectorRow label="Show">
          <SelectField<LineDetailsMode>
            value={col.detailsMode ?? 'fromTo'}
            options={LINE_DETAILS_MODES.map((v) => ({
              value: v,
              label: LINE_DETAILS_MODE_LABEL[v],
            }))}
            onChange={(detailsMode) => onChange({ ...col, detailsMode })}
          />
        </InspectorRow>
        <p className="mt-0.5 text-[10px] leading-snug text-slate-400">
          Additional charge rows always show their own charge text here, so a fuel surcharge still
          says what it is. Pick “Charge description” to use the charge text on every row.
        </p>
      </>
    );
  }

  if (col.key === 'orderRef') {
    return (
      <>
        <InspectorRow label="Reference slot">
          <SelectField<string>
            value={String(col.referenceSlot ?? 1)}
            options={REFERENCE_SLOTS.map((slot) => ({
              value: String(slot),
              // The organisation names these slots on the Organisation page, so
              // show its wording rather than "Reference 2".
              label: referenceLabels[slot - 1]?.trim() || `Reference ${slot}`,
            }))}
            onChange={(value) => onChange({ ...col, referenceSlot: Number(value) })}
          />
        </InspectorRow>
        <p className="mt-0.5 text-[10px] leading-snug text-slate-400">
          Slot names come from Settings → Organisation.
        </p>
      </>
    );
  }

  if (col.key === 'cargo') {
    return (
      <InspectorRow label="Show">
        <SelectField<LineCargoMode>
          value={col.cargoMode ?? 'both'}
          options={LINE_CARGO_MODES.map((v) => ({ value: v, label: LINE_CARGO_MODE_LABEL[v] }))}
          onChange={(cargoMode) => onChange({ ...col, cargoMode })}
        />
      </InspectorRow>
    );
  }

  return null;
}

export function LinesBandInspector({
  band,
  page,
  referenceLabels,
  onChange,
}: {
  band: LinesBand;
  page: PageSettings;
  /** The organisation's reference-slot names, positionally (slot 1 at index 0). */
  referenceLabels: string[];
  onChange: (next: LinesBand) => void;
}) {
  const visibleCount = band.columns.filter((c) => c.visible).length;
  const flexKey = resolveFlexKey(band.columns);
  const fit = flexKey ? measureColumns(band.columns, page, flexKey) : null;

  const patchColumn = (next: LineColumn) =>
    onChange({ ...band, columns: band.columns.map((c) => (c.key === next.key ? next : c)) });

  // The products setting shows only for an organisation that uses products.
  const { user } = useAuth0();
  const organisationQuery = useOrganisation(getCurrentOrganisationId(user) || null);
  const productsEnabled = organisationQuery.data?.useProducts ?? false;
  const productsDisplay: ProductsDisplay = band.productsDisplay ?? 'hidden';

  // 'Own column' needs the column in the list (layouts saved before it existed
  // have none) and ticked, so it can be moved and sized like the rest; the
  // other two untick it.
  const setProductsDisplay = (next: ProductsDisplay) => {
    const has = band.columns.some((c) => c.key === 'products');
    let columns = band.columns.map((c) =>
      c.key === 'products' ? { ...c, visible: next === 'column' } : c,
    );
    if (!has && next === 'column') {
      const at = columns.findIndex((c) => c.key === 'net');
      const col: LineColumn = { key: 'products', label: 'Products', visible: true, widthPt: 120, align: 'left' };
      columns = at < 0 ? [...columns, col] : [...columns.slice(0, at), col, ...columns.slice(at)];
    }
    onChange({ ...band, productsDisplay: next, columns });
  };

  return (
    <>
      <InspectorGroup title="Table">
        <InspectorRow label="Header row">
          <Checkbox
            checked={band.showHeader}
            label=""
            onChange={(showHeader) => onChange({ ...band, showHeader })}
          />
        </InspectorRow>
        <InspectorRow label="Striped rows">
          <Checkbox
            checked={band.zebra}
            label=""
            onChange={(zebra) => onChange({ ...band, zebra })}
          />
        </InspectorRow>
        <InspectorRow
          label="Group charges under their order"
          hint="Blank the repeated date, order number and transport cells"
        >
          <Checkbox
            checked={band.blankRepeatedContext ?? true}
            label=""
            onChange={(blankRepeatedContext) => onChange({ ...band, blankRepeatedContext })}
          />
        </InspectorRow>
        <InspectorRow label="Order subheading rows" hint="An “ORD-123” row above each group">
          <Checkbox
            checked={band.groupByOrder}
            label=""
            onChange={(groupByOrder) => onChange({ ...band, groupByOrder })}
          />
        </InspectorRow>
        {(productsEnabled || productsDisplay !== 'hidden') && (
          <InspectorRow
            label="Products"
            hint="The goods each transport line carried, e.g. “Sulphuric Acid (UN 1830)”"
          >
            <SelectField
              value={productsDisplay}
              options={PRODUCTS_DISPLAYS.map((v) => ({ value: v, label: PRODUCTS_DISPLAY_LABEL[v] }))}
              onChange={setProductsDisplay}
            />
          </InspectorRow>
        )}
      </InspectorGroup>

      <InspectorGroup title="Columns (left to right)">
        {fit && flexKey && (
          <p
            className={cn(
              'mb-1.5 rounded px-1.5 py-1 text-[10px] leading-snug',
              fit.tooNarrow ? 'bg-red-50 text-red-700' : 'text-slate-400',
            )}
          >
            {fit.tooNarrow
              ? `${LINE_COLUMN_LABEL[flexKey]} is down to ${Math.round(
                  fit.flexRemainingPt,
                )}pt — text will break mid-word. Hide a column or reduce a width.`
              : `${LINE_COLUMN_LABEL[flexKey]} gets the remaining ${Math.round(
                  fit.flexRemainingPt,
                )}pt of ${Math.round(fit.availablePt)}pt.`}
          </p>
        )}

        <p className="mb-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] leading-snug text-amber-800">
          To move a column, untick it first, move it, then tick it again.
        </p>

        <div className="space-y-1">
          {band.columns.map((col, i) => (
            <div
              key={col.key}
              className={cn(
                'rounded border p-1.5',
                col.visible
                  ? 'border-slate-200 bg-white'
                  : 'border-dashed border-slate-200 bg-slate-50',
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <Checkbox
                  checked={col.visible}
                  label={LINE_COLUMN_LABEL[col.key]}
                  // The table needs at least one column; block unticking the last.
                  disabled={col.visible && visibleCount === 1}
                  onChange={(visible) => patchColumn({ ...col, visible })}
                />
                {/* Reordering is offered only while the column is switched
                    off — see the note under the list. The placeholder keeps the
                    rows the same width so the list does not shift as columns
                    are ticked. */}
                {col.visible ? (
                  <span className="w-3 shrink-0" aria-hidden="true" />
                ) : (
                  <MoveButtons
                    canUp={i > 0}
                    canDown={i < band.columns.length - 1}
                    onUp={() => onChange({ ...band, columns: moveItem(band.columns, i, -1) })}
                    onDown={() => onChange({ ...band, columns: moveItem(band.columns, i, 1) })}
                  />
                )}
              </div>

              {col.visible && (
                <div className="mt-1 space-y-0.5 pl-5">
                  <div className="flex items-center gap-1.5">
                    <TextField
                      className="w-24"
                      value={col.label}
                      placeholder={LINE_COLUMN_LABEL[col.key]}
                      onChange={(label) => patchColumn({ ...col, label })}
                    />
                    {col.key === flexKey ? (
                      <span className="text-[10px] text-slate-400">fills width</span>
                    ) : (
                      <NumberField
                        value={col.widthPt}
                        min={24}
                        max={220}
                        suffix="pt"
                        onChange={(widthPt) => patchColumn({ ...col, widthPt })}
                      />
                    )}
                    <AlignPicker
                      value={col.align}
                      onChange={(align) => patchColumn({ ...col, align })}
                    />
                  </div>
                  <ColumnOptions
                    col={col}
                    referenceLabels={referenceLabels}
                    onChange={patchColumn}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </InspectorGroup>
    </>
  );
}
